import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet } from 'react-router';

import { AppLayout } from '@/components/layout/app-layout';
import { RequireAuth } from '@/components/layout/require-auth';
import { RequirePermission } from '@/components/layout/require-permission';
import { RouteFallback } from '@/components/layout/route-fallback';
import { NotFoundPage } from '@/pages/not-found';

/**
 * Application routes.
 *
 * Every screen below the shell is lazily loaded. This is not premature: the
 * kiosk, waiting-area display and counter terminal run on modest branch hardware
 * over a branch connection, and they must not download the complaints workspace,
 * the survey builder or Recharts to show a queue number.
 *
 * Route-level permission gates mirror the server's, purely so a user never
 * navigates into a screen that will only 403 on its first request. The server
 * remains the authority.
 */

const LoginPage = lazy(() => import('@/pages/auth/login'));
const DashboardPage = lazy(() => import('@/pages/dashboard'));
const SettingsPage = lazy(() => import('@/pages/admin/settings'));
const BrandingPage = lazy(() => import('@/pages/admin/branding'));
const UsersPage = lazy(() => import('@/pages/admin/users'));
const RolesPage = lazy(() => import('@/pages/admin/roles'));
const AuditPage = lazy(() => import('@/pages/admin/audit'));
const HealthPage = lazy(() => import('@/pages/admin/health'));
const CompliancePage = lazy(() => import('@/pages/admin/compliance'));
const DemoControlPage = lazy(() => import('@/pages/admin/demo'));
const ShowcasePage = lazy(() => import('@/pages/showcase'));
const GuidePage = lazy(() => import('@/pages/guide'));
const ApiDocsPage = lazy(() => import('@/pages/docs/api'));
const EventsPage = lazy(() => import('@/pages/admin/events'));
const CustomersPage = lazy(() => import('@/pages/customers/index'));
const ComplaintsPage = lazy(() => import('@/pages/complaints/index'));
const ViewLayoutsPage = lazy(() => import('@/pages/admin/view-layouts'));
const AiSettingsPage = lazy(() => import('@/pages/admin/ai-settings'));
const AiUsagePage = lazy(() => import('@/pages/admin/ai-usage'));
const KnowledgePage = lazy(() => import('@/pages/admin/knowledge'));
const ComplaintConfigurationPage = lazy(
    () => import('@/pages/admin/complaint-configuration'),
);
const ComplaintShowPage = lazy(() => import('@/pages/complaints/show'));
const PublicComplaintStatusPage = lazy(
    () => import('@/pages/public/complaint-status'),
);
const CustomerShowPage = lazy(() => import('@/pages/customers/show'));
const SegmentsPage = lazy(() => import('@/pages/customers/segments'));
const CustomerImportPage = lazy(() => import('@/pages/customers/import'));
const QueueDashboardPage = lazy(() => import('@/pages/queue/dashboard'));
const QueueAppointmentsPage = lazy(() => import('@/pages/queue/appointments'));
const QueueHardwarePage = lazy(() => import('@/pages/queue/hardware'));
const QueueConfigurationPage = lazy(() => import('@/pages/queue/configuration'));
const QueueSimulatorPage = lazy(() => import('@/pages/queue/simulator'));
const CounterPage = lazy(() => import('@/pages/queue/counter'));
const CounterByCodePage = lazy(() => import('@/pages/queue/counter-by-code'));
const KioskPage = lazy(() => import('@/pages/queue/kiosk'));
const DisplayPage = lazy(() => import('@/pages/queue/display'));
const SoftQueuePage = lazy(() => import('@/pages/public/soft-queue'));
const ChatWidgetPage = lazy(() => import('@/pages/public/chat-widget'));
const PublicSurveyPage = lazy(() => import('@/pages/public/survey'));
const ExperienceDashboardPage = lazy(() => import('@/pages/measurement/dashboard'));
const SurveysPage = lazy(() => import('@/pages/measurement/surveys'));
const SurveyBuilderPage = lazy(() => import('@/pages/measurement/survey-builder'));
const DistributionsPage = lazy(() => import('@/pages/measurement/distributions'));
const JourneysPage = lazy(() => import('@/pages/measurement/journeys'));
const VerbatimsPage = lazy(() => import('@/pages/measurement/verbatims'));
const ImprovementActionsPage = lazy(() => import('@/pages/measurement/actions'));
const SurveySimulatorsPage = lazy(() => import('@/pages/measurement/simulators'));
const IntegrationsPage = lazy(() => import('@/pages/integrations/index'));
const IntegrationHealthPage = lazy(() => import('@/pages/integrations/health'));
const WebhooksPage = lazy(() => import('@/pages/integrations/webhooks'));
const ApiKeysPage = lazy(() => import('@/pages/integrations/api-keys'));
const IntegrationSimulatorsPage = lazy(() => import('@/pages/integrations/simulators'));
const ExecutiveDashboardPage = lazy(() => import('@/pages/reporting/executive'));
const ReportsPage = lazy(() => import('@/pages/reporting/reports'));
const ReportBuilderPage = lazy(() => import('@/pages/reporting/builder'));
const ReportSchedulesPage = lazy(() => import('@/pages/reporting/schedules'));
const ReportRunsPage = lazy(() => import('@/pages/reporting/runs'));
const AgentConsolePage = lazy(() => import('@/pages/contact-centre/console'));
const CcWallboardPage = lazy(() => import('@/pages/contact-centre/wallboard'));
const CcReportingPage = lazy(() => import('@/pages/contact-centre/reporting'));
const CcConfigurationPage = lazy(() => import('@/pages/contact-centre/configuration'));
const CcChatbotPage = lazy(() => import('@/pages/contact-centre/chatbot'));
const CcIvrPage = lazy(() => import('@/pages/contact-centre/ivr'));
const CcLicensingPage = lazy(() => import('@/pages/contact-centre/licensing'));

function LazyOutlet() {
    return (
        <Suspense fallback={<RouteFallback />}>
            <Outlet />
        </Suspense>
    );
}

export const router = createBrowserRouter([
    {
        path: '/login',
        element: (
            <Suspense fallback={<RouteFallback />}>
                <LoginPage />
            </Suspense>
        ),
    },
    {
        /*
         * Outside the authenticated shell on purpose. A customer reaches this
         * from a link in an SMS, with no session and no expectation of one —
         * putting it inside RequireAuth would bounce them to a login screen for
         * an account they do not have.
         */
        path: '/complaint-status',
        element: (
            <Suspense fallback={<RouteFallback />}>
                <PublicComplaintStatusPage />
            </Suspense>
        ),
    },
    {
        /*
         * Branch hardware, outside the authenticated shell and outside the app
         * chrome. A kiosk authenticates as a *device*, not as whoever last
         * signed in on it, and it renders full-bleed — a sidebar and a user menu
         * on a machine in a public hall are both useless and a way in.
         */
        path: '/kiosk/:branchId',
        element: (
            <Suspense fallback={<RouteFallback />}>
                <KioskPage />
            </Suspense>
        ),
    },
    {
        path: '/display/:branchId',
        element: (
            <Suspense fallback={<RouteFallback />}>
                <DisplayPage />
            </Suspense>
        ),
    },
    {
        /*
         * The web chat widget. Public and outside the app chrome: a visitor on
         * the Bank's website has no account, and the widget is embedded in a page
         * that already has its own header.
         */
        path: '/chat',
        element: (
            <Suspense fallback={<RouteFallback />}>
                <ChatWidgetPage />
            </Suspense>
        ),
    },
    {
        // Joining the queue from a phone. Public, like the complaint status page.
        path: '/queue/join',
        element: (
            <Suspense fallback={<RouteFallback />}>
                <SoftQueuePage />
            </Suspense>
        ),
    },
    {
        /*
         * The survey a customer answers. Public and outside the app shell: the
         * token in the path is the whole authorisation, and somebody who scanned
         * a poster on a branch counter must never be shown a back office.
         */
        path: '/survey/:token',
        element: (
            <Suspense fallback={<RouteFallback />}>
                <PublicSurveyPage />
            </Suspense>
        ),
    },
    {
        element: (
            <RequireAuth>
                <AppLayout>
                    <LazyOutlet />
                </AppLayout>
            </RequireAuth>
        ),
        children: [
            { index: true, element: <Navigate to="/dashboard" replace /> },
            { path: 'dashboard', element: <DashboardPage /> },
            /*
             * No permission gate. The showcase shows a role its own tour and
             * counts of records it can already reach; the server withholds the
             * parts it should not see.
             */
            { path: 'showcase', element: <ShowcasePage /> },
            /*
             * The user guide. No permission gate: it is the document somebody's
             * first week is spent in, and the server tailors it to their role.
             */
            { path: 'guide', element: <GuidePage /> },
            /*
             * The address TEC-DOC-2 names. Authenticated and nothing further:
             * it documents the interface the reader's own browser is calling.
             */
            { path: 'docs/api', element: <ApiDocsPage /> },
            {
                path: 'customers',
                children: [
                    {
                        index: true,
                        element: (
                            <RequirePermission permission="customers.view">
                                <CustomersPage />
                            </RequirePermission>
                        ),
                    },
                    {
                        // Before `:id`, or "segments" is captured as a customer id.
                        path: 'segments',
                        element: (
                            <RequirePermission permission="customers.view">
                                <SegmentsPage />
                            </RequirePermission>
                        ),
                    },
                    {
                        path: 'import',
                        element: (
                            <RequirePermission permission="customers.import">
                                <CustomerImportPage />
                            </RequirePermission>
                        ),
                    },
                    {
                        path: ':id',
                        element: (
                            <RequirePermission permission="customers.view">
                                <CustomerShowPage />
                            </RequirePermission>
                        ),
                    },
                ],
            },
            {
                path: 'complaints',
                children: [
                    {
                        index: true,
                        element: (
                            <RequirePermission
                                anyOf={[
                                    'complaints.view.own',
                                    'complaints.view.team',
                                    'complaints.view.all',
                                ]}
                            >
                                <ComplaintsPage />
                            </RequirePermission>
                        ),
                    },
                    {
                        path: ':id',
                        element: (
                            <RequirePermission
                                anyOf={[
                                    'complaints.view.own',
                                    'complaints.view.team',
                                    'complaints.view.all',
                                ]}
                            >
                                <ComplaintShowPage />
                            </RequirePermission>
                        ),
                    },
                ],
            },
            {
                /*
                 * The address §5 gives the counter terminal. Inside the
                 * authenticated shell, unlike the kiosk and the display: a
                 * counter is operated by a signed-in agent sitting behind it,
                 * not by unattended hardware in a public hall.
                 */
                path: 'counter/:branchCode/:counterCode',
                element: (
                    <RequirePermission permission="queue.counter.operate">
                        <CounterByCodePage />
                    </RequirePermission>
                ),
            },
            {
                path: 'reports',
                children: [
                    {
                        index: true,
                        element: (
                            <RequirePermission permission="reports.view">
                                <ReportsPage />
                            </RequirePermission>
                        ),
                    },
                    {
                        path: 'executive',
                        element: (
                            <RequirePermission permission="reports.view">
                                <ExecutiveDashboardPage />
                            </RequirePermission>
                        ),
                    },
                    {
                        path: 'builder/:reportId',
                        element: (
                            <RequirePermission permission="reports.build">
                                <ReportBuilderPage />
                            </RequirePermission>
                        ),
                    },
                    {
                        path: 'schedules',
                        element: (
                            <RequirePermission permission="reports.schedule">
                                <ReportSchedulesPage />
                            </RequirePermission>
                        ),
                    },
                    {
                        path: 'history',
                        element: (
                            <RequirePermission permission="reports.view">
                                <ReportRunsPage />
                            </RequirePermission>
                        ),
                    },
                ],
            },
            {
                /*
                 * The demonstration control panel (§11.2). Gated on the
                 * permission here and on demonstration mode at the server, which
                 * answers 404 rather than 403 on an installation that is not one.
                 */
                path: 'admin/demo',
                element: (
                    <RequirePermission permission="demo.control">
                        <DemoControlPage />
                    </RequirePermission>
                ),
            },
            {
                path: 'admin/integrations',
                children: [
                    {
                        index: true,
                        element: (
                            <RequirePermission
                                anyOf={['integrations.view', 'integrations.manage']}
                            >
                                <IntegrationsPage />
                            </RequirePermission>
                        ),
                    },
                    {
                        path: 'health',
                        element: (
                            <RequirePermission
                                anyOf={['integrations.view', 'integrations.manage']}
                            >
                                <IntegrationHealthPage />
                            </RequirePermission>
                        ),
                    },
                    {
                        path: 'webhooks',
                        element: (
                            <RequirePermission
                                anyOf={['integrations.view', 'integrations.manage']}
                            >
                                <WebhooksPage />
                            </RequirePermission>
                        ),
                    },
                    {
                        path: 'simulators',
                        element: (
                            <RequirePermission
                                anyOf={['integrations.view', 'integrations.manage']}
                            >
                                <IntegrationSimulatorsPage />
                            </RequirePermission>
                        ),
                    },
                    {
                        path: 'api-keys',
                        element: (
                            <RequirePermission permission="integrations.keys.manage">
                                <ApiKeysPage />
                            </RequirePermission>
                        ),
                    },
                ],
            },
            {
                path: 'measurement',
                children: [
                    {
                        index: true,
                        element: (
                            <RequirePermission permission="measurement.analytics.view">
                                <ExperienceDashboardPage />
                            </RequirePermission>
                        ),
                    },
                    {
                        path: 'surveys',
                        element: (
                            <RequirePermission permission="surveys.view">
                                <SurveysPage />
                            </RequirePermission>
                        ),
                    },
                    {
                        path: 'surveys/:surveyId',
                        element: (
                            <RequirePermission permission="surveys.design">
                                <SurveyBuilderPage />
                            </RequirePermission>
                        ),
                    },
                    {
                        path: 'distributions',
                        element: (
                            <RequirePermission permission="surveys.distribute">
                                <DistributionsPage />
                            </RequirePermission>
                        ),
                    },
                    {
                        path: 'journeys',
                        element: (
                            <RequirePermission permission="surveys.journeys.manage">
                                <JourneysPage />
                            </RequirePermission>
                        ),
                    },
                    {
                        path: 'verbatims',
                        element: (
                            <RequirePermission permission="measurement.verbatims.view">
                                <VerbatimsPage />
                            </RequirePermission>
                        ),
                    },
                    {
                        path: 'actions',
                        element: (
                            <RequirePermission permission="measurement.actions.manage">
                                <ImprovementActionsPage />
                            </RequirePermission>
                        ),
                    },
                    {
                        path: 'channels',
                        element: (
                            <RequirePermission permission="surveys.distribute">
                                <SurveySimulatorsPage />
                            </RequirePermission>
                        ),
                    },
                ],
            },
            {
                path: 'contact-centre',
                children: [
                    {
                        index: true,
                        element: (
                            <RequirePermission permission="cc.console.use">
                                <AgentConsolePage />
                            </RequirePermission>
                        ),
                    },
                    {
                        path: 'wallboard',
                        element: (
                            <RequirePermission permission="cc.wallboard.view">
                                <CcWallboardPage />
                            </RequirePermission>
                        ),
                    },
                    {
                        path: 'reporting',
                        element: (
                            <RequirePermission permission="cc.wallboard.view">
                                <CcReportingPage />
                            </RequirePermission>
                        ),
                    },
                    {
                        path: 'configuration',
                        element: (
                            <RequirePermission
                                anyOf={['cc.routing.manage', 'cc.agents.manage']}
                            >
                                <CcConfigurationPage />
                            </RequirePermission>
                        ),
                    },
                    {
                        path: 'chatbot',
                        element: (
                            <RequirePermission permission="cc.chatbot.manage">
                                <CcChatbotPage />
                            </RequirePermission>
                        ),
                    },
                    {
                        path: 'ivr',
                        element: (
                            <RequirePermission permission="cc.ivr.manage">
                                <CcIvrPage />
                            </RequirePermission>
                        ),
                    },
                    {
                        path: 'licensing',
                        element: (
                            <RequirePermission permission="cc.licensing.manage">
                                <CcLicensingPage />
                            </RequirePermission>
                        ),
                    },
                ],
            },
            {
                path: 'queue',
                children: [
                    {
                        index: true,
                        element: (
                            <RequirePermission permission="queue.analytics.view">
                                <QueueDashboardPage />
                            </RequirePermission>
                        ),
                    },
                    {
                        path: 'appointments',
                        element: (
                            <RequirePermission permission="queue.appointments.view">
                                <QueueAppointmentsPage />
                            </RequirePermission>
                        ),
                    },
                    {
                        path: 'configuration',
                        element: (
                            <RequirePermission
                                anyOf={['queue.services.manage', 'queue.branches.manage']}
                            >
                                <QueueConfigurationPage />
                            </RequirePermission>
                        ),
                    },
                    {
                        path: 'hardware',
                        element: (
                            <RequirePermission permission="queue.hardware.manage">
                                <QueueHardwarePage />
                            </RequirePermission>
                        ),
                    },
                    {
                        path: 'simulator',
                        element: (
                            <RequirePermission permission="queue.tickets.manage">
                                <QueueSimulatorPage />
                            </RequirePermission>
                        ),
                    },
                    {
                        // The counter terminal keeps the app chrome: it is a
                        // staff screen behind a counter, operated by a signed-in
                        // agent, not unattended hardware.
                        path: 'counters/:counterId',
                        element: (
                            <RequirePermission permission="queue.counter.operate">
                                <CounterPage />
                            </RequirePermission>
                        ),
                    },
                ],
            },
            {
                path: 'admin',
                children: [
                    { index: true, element: <Navigate to="/admin/health" replace /> },
                    {
                        path: 'settings',
                        element: (
                            <RequirePermission permission="settings.manage">
                                <SettingsPage />
                            </RequirePermission>
                        ),
                    },
                    {
                        path: 'branding',
                        element: (
                            <RequirePermission permission="branding.manage">
                                <BrandingPage />
                            </RequirePermission>
                        ),
                    },
                    {
                        path: 'users',
                        element: (
                            <RequirePermission permission="users.view">
                                <UsersPage />
                            </RequirePermission>
                        ),
                    },
                    {
                        path: 'roles',
                        element: (
                            <RequirePermission permission="roles.manage">
                                <RolesPage />
                            </RequirePermission>
                        ),
                    },
                    {
                        path: 'audit',
                        element: (
                            <RequirePermission permission="audit.view">
                                <AuditPage />
                            </RequirePermission>
                        ),
                    },
                    {
                        path: 'health',
                        element: (
                            <RequirePermission permission="system.health.view">
                                <HealthPage />
                            </RequirePermission>
                        ),
                    },
                    {
                        path: 'events',
                        element: (
                            <RequirePermission permission="system.health.view">
                                <EventsPage />
                            </RequirePermission>
                        ),
                    },
                    {
                        path: 'ai',
                        element: (
                            <RequirePermission permission="ai.settings.manage">
                                <AiSettingsPage />
                            </RequirePermission>
                        ),
                    },
                    {
                        path: 'ai-usage',
                        element: (
                            <RequirePermission permission="ai.usage.view">
                                <AiUsagePage />
                            </RequirePermission>
                        ),
                    },
                    {
                        path: 'knowledge',
                        element: (
                            <RequirePermission permission="ai.knowledge.manage">
                                <KnowledgePage />
                            </RequirePermission>
                        ),
                    },
                    {
                        path: 'view-layouts',
                        element: (
                            <RequirePermission permission="view_layouts.manage">
                                <ViewLayoutsPage />
                            </RequirePermission>
                        ),
                    },
                    {
                        path: 'complaint-configuration',
                        element: (
                            <RequirePermission
                                anyOf={[
                                    'complaints.routing.manage',
                                    'complaints.sla.manage',
                                    'complaints.categories.manage',
                                ]}
                            >
                                <ComplaintConfigurationPage />
                            </RequirePermission>
                        ),
                    },
                    {
                        path: 'compliance',
                        element: (
                            <RequirePermission permission="rfp.matrix.view">
                                <CompliancePage />
                            </RequirePermission>
                        ),
                    },
                ],
            },
            { path: '*', element: <NotFoundPage /> },
        ],
    },
]);
